import { useState, useRef } from "react";
import { FileAttachment } from "@/types/attachment";
import { FileAttachmentCard } from "./FileAttachmentCard";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Paperclip, Plus } from "lucide-react";

interface AttachmentsPanelProps {
  attachments: FileAttachment[];
  onDeleteAttachment?: (id: string) => void;
  onHighlightAttachment?: (id: string) => void;
  onAddAttachment?: (files: File[]) => void;
  highlightedAttachmentId?: string;
}

export function AttachmentsPanel({
  attachments,
  onDeleteAttachment,
  onHighlightAttachment,
  onAddAttachment,
  highlightedAttachmentId
}: AttachmentsPanelProps) {
  const activeAttachments = attachments.filter(a => !a.deleted);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && onAddAttachment) {
      onAddAttachment(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onAddAttachment) {
      onAddAttachment(Array.from(files));
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddClick = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <div 
      className="h-full flex flex-col bg-background"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="p-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Attachments</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {activeAttachments.length}
          </Badge>
          {onAddAttachment && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddClick}
              className="h-7 px-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          )}
        </div>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
        aria-label="Upload attachment files"
      />
      
      <div className={`flex-1 relative ${isDragging ? 'bg-primary/5' : ''}`}>
        {isDragging && (
          <div className="absolute inset-0 border-2 border-dashed border-primary pointer-events-none z-10 flex items-center justify-center">
            <div className="bg-background/90 px-4 py-2 rounded-md shadow-lg">
              <p className="text-sm font-medium">Drop files to attach</p>
            </div>
          </div>
        )}
        <ScrollArea className="h-full">
          <div className="p-3 space-y-3">
            {activeAttachments.length === 0 ? (
              <div className="text-center text-muted-foreground text-sm py-8">
                <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No attachments</p>
                <p className="text-xs mt-1">Click "Add" or drop files here</p>
              </div>
            ) : (
              activeAttachments.map((attachment) => (
                <FileAttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  onDelete={onDeleteAttachment}
                  onHighlight={onHighlightAttachment}
                  highlighted={highlightedAttachmentId === attachment.id}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
